// src/index.ts
export * from "@qr20/types";

// src/identity-attestation.ts
var qr20NoopAttestationProvider = {
  name: "qr20-noop",
  async resolveAttestations() {
    return { status: "not_found" };
  }
};

// src/jcs-dispatch.ts
var wasmFn = null;
var resolved = false;
var inflight = null;
function ensureCanonicalizerReady() {
  if (resolved) {
    return Promise.resolve(wasmFn !== null ? "wasm" : "ts");
  }
  if (inflight !== null) {
    return inflight;
  }
  inflight = (async () => {
    try {
      const specifier = "@qr20/core-wasm";
      const mod = await import(
        /* @vite-ignore */
        specifier
      );
      if (typeof mod.ensureWasmReady === "function") {
        await mod.ensureWasmReady();
      }
      if (typeof mod.canonicalizeQr20JsonWasm !== "function") {
        throw new Error(
          "@qr20/core-wasm export shape is unexpected: canonicalizeQr20JsonWasm is missing."
        );
      }
      wasmFn = mod.canonicalizeQr20JsonWasm;
      resolved = true;
      return "wasm";
    } catch {
      wasmFn = null;
      resolved = true;
      return "ts";
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
function getWasmCanonicalizer() {
  return wasmFn;
}
function __resetCanonicalizerForTest() {
  wasmFn = null;
  resolved = false;
  inflight = null;
}

// src/index.ts
var qr20RoutingModes = ["direct", "priority_list", "capability_match", "auction", "broadcast"];
var qr20EncryptionMethods = ["none", "asymmetric", "symmetric", "lit_protocol", "zk"];
var qr20ConditionComparators = [">", "<", "=", "!=", "contains"];
var qr20FailureModes = ["abort", "skip", "fallback"];
var qr20ExecutionModes = ["sequential", "parallel", "dag"];
var qr20AtomicityModes = ["all_or_nothing", "best_effort"];
var qr20AgentVerificationMethods = ["on_chain_hash", "zk_proof", "oracle_attestation", "merkle_receipt"];
var qr20ProofTypes = ["tx_hash", "merkle_receipt", "zk_proof", "oracle_attestation", "signed_output"];
var qr20ConditionTypes = ["oracle", "on_chain_state", "time", "agent_output"];
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isJsonValue(value) {
  if (value === null) {
    return true;
  }
  if (typeof value === "string" || typeof value === "boolean") {
    return true;
  }
  if (typeof value === "number") {
    return Number.isFinite(value);
  }
  if (Array.isArray(value)) {
    return value.every((entry) => isJsonValue(entry));
  }
  if (!isRecord(value)) {
    return false;
  }
  return Object.values(value).every((entry) => isJsonValue(entry));
}
function pushError(errors, path, message) {
  errors.push({ path, message });
}
function expectRecord(value, path, errors) {
  if (!isRecord(value)) {
    pushError(errors, path, "Expected an object.");
    return void 0;
  }
  return value;
}
function expectArray(value, path, errors) {
  if (!Array.isArray(value)) {
    pushError(errors, path, "Expected an array.");
    return void 0;
  }
  return value;
}
function expectString(value, path, errors, allowEmpty = false) {
  if (typeof value !== "string") {
    pushError(errors, path, "Expected a string.");
    return void 0;
  }
  if (!allowEmpty && value.trim().length === 0) {
    pushError(errors, path, "Expected a non-empty string.");
    return void 0;
  }
  return value;
}
function expectInteger(value, path, errors, minimum) {
  if (!Number.isInteger(value)) {
    pushError(errors, path, "Expected an integer.");
    return void 0;
  }
  if (minimum !== void 0 && value < minimum) {
    pushError(errors, path, `Expected an integer >= ${minimum}.`);
    return void 0;
  }
  return value;
}
function expectStringArray(value, path, errors) {
  const array = expectArray(value, path, errors);
  if (!array) {
    return void 0;
  }
  const result2 = [];
  array.forEach((entry, index) => {
    const parsed = expectString(entry, `${path}[${index}]`, errors);
    if (parsed !== void 0) {
      result2.push(parsed);
    }
  });
  return result2;
}
function rejectUnknownKeys(record, allowedKeys, path, errors) {
  const allowed = new Set(allowedKeys);
  Object.keys(record).forEach((key) => {
    if (!allowed.has(key)) {
      pushError(errors, `${path}.${key}`, "Unexpected field.");
    }
  });
}
function expectEnum(value, path, errors, allowedValues) {
  const parsed = expectString(value, path, errors);
  if (parsed === void 0) {
    return void 0;
  }
  if (!allowedValues.includes(parsed)) {
    pushError(errors, path, `Expected one of: ${allowedValues.join(", ")}.`);
    return void 0;
  }
  return parsed;
}
function expectIsoTimestamp(value, path, errors) {
  const parsed = expectString(value, path, errors);
  if (parsed === void 0) {
    return void 0;
  }
  if (Number.isNaN(Date.parse(parsed))) {
    pushError(errors, path, "Expected a valid ISO-8601 timestamp.");
    return void 0;
  }
  return parsed;
}
var uuidV4Pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
var hashIdentifierPattern = /^(sha256:)?[0-9a-f]{64}$/i;
function expectIntentIdentifier(value, path, errors) {
  const parsed = expectString(value, path, errors);
  if (parsed === void 0) {
    return void 0;
  }
  if (!uuidV4Pattern.test(parsed) && !hashIdentifierPattern.test(parsed)) {
    pushError(errors, path, "Expected a UUID v4 or a SHA-256 content hash.");
    return void 0;
  }
  return parsed;
}
function result(value, errors) {
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, value, errors: [] };
}
function validateCreator(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["address", "signature", "chain", "encoding"], path, errors);
  expectString(record.address, `${path}.address`, errors);
  expectString(record.signature, `${path}.signature`, errors);
  expectString(record.chain, `${path}.chain`, errors);
  if (record.encoding !== void 0) {
    if (record.encoding !== "eip712") {
      pushError(errors, `${path}.encoding`, 'Expected encoding to be "eip712" when present.');
    }
  }
}
function validateRouting(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["mode", "target_resolvers", "required_capabilities", "deadline", "max_fee"], path, errors);
  expectEnum(record.mode, `${path}.mode`, errors, qr20RoutingModes);
  expectStringArray(record.target_resolvers, `${path}.target_resolvers`, errors);
  expectStringArray(record.required_capabilities, `${path}.required_capabilities`, errors);
  const deadlinePath = `${path}.deadline`;
  const deadline = record.deadline;
  if (typeof deadline === "number") {
    expectInteger(deadline, deadlinePath, errors, 0);
  } else {
    expectIsoTimestamp(deadline, deadlinePath, errors);
  }
  expectString(record.max_fee, `${path}.max_fee`, errors);
}
function validateEncryption(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["method", "params"], path, errors);
  expectEnum(record.method, `${path}.method`, errors, qr20EncryptionMethods);
  const params = expectRecord(record.params, `${path}.params`, errors);
  if (params) {
    Object.entries(params).forEach(([key, entry]) => {
      if (!isJsonValue(entry)) {
        pushError(errors, `${path}.params.${key}`, "Expected a JSON-serializable value.");
      }
    });
  }
}
var QR20_REMOVED_FIELD_MIGRATION_CODE = "ENVELOPE_FIELD_REMOVED_V1_1";
var QR20_REMOVED_ENVELOPE_FIELDS = {
  affiliate_block: `${QR20_REMOVED_FIELD_MIGRATION_CODE}: affiliate_block was removed from the universal envelope in v1.1. Attribution is now carried by a separate Type 16 intent under QIP-001. Drop this field and, if attribution is required, emit a companion Type 16 envelope (specification pending) or track attribution out-of-band keyed by the envelope content id.`
};
function validateRemovedEnvelopeFields(record, errors) {
  for (const field of Object.keys(QR20_REMOVED_ENVELOPE_FIELDS)) {
    if (field in record) {
      const message = QR20_REMOVED_ENVELOPE_FIELDS[field];
      if (message !== void 0) {
        pushError(errors, `$.${field}`, message);
      }
    }
  }
}
function validateEnvelopeProof(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["resolver", "tx_hash", "merkle_root", "zk_proof", "timestamp"], path, errors);
  expectString(record.resolver, `${path}.resolver`, errors);
  expectIsoTimestamp(record.timestamp, `${path}.timestamp`, errors);
  const proofFields = ["tx_hash", "merkle_root", "zk_proof"].filter((field) => typeof record[field] === "string");
  if (proofFields.length === 0) {
    pushError(errors, path, "Expected at least one proof artifact: tx_hash, merkle_root, or zk_proof.");
  }
  if (record.tx_hash !== void 0) {
    expectString(record.tx_hash, `${path}.tx_hash`, errors);
  }
  if (record.merkle_root !== void 0) {
    expectString(record.merkle_root, `${path}.merkle_root`, errors);
  }
  if (record.zk_proof !== void 0) {
    expectString(record.zk_proof, `${path}.zk_proof`, errors);
  }
}
function validateChannel(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["channel_id", "sequence"], path, errors);
  expectString(record.channel_id, `${path}.channel_id`, errors);
  expectInteger(record.sequence, `${path}.sequence`, errors, 0);
}
function validateType0Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "to", "chain_id", "token", "amount", "memo"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "transfer") {
    pushError(errors, `${path}.action`, 'Expected action to be "transfer".');
  }
  expectString(record.to, `${path}.to`, errors);
  expectString(record.chain_id, `${path}.chain_id`, errors);
  expectString(record.token, `${path}.token`, errors);
  expectString(record.amount, `${path}.amount`, errors);
  if (record.memo !== void 0) {
    expectString(record.memo, `${path}.memo`, errors);
  }
}
function validateType1Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "recipient_pubkey", "ciphertext", "algorithm", "ephemeral_pubkey"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "decrypt_message") {
    pushError(errors, `${path}.action`, 'Expected action to be "decrypt_message".');
  }
  expectString(record.recipient_pubkey, `${path}.recipient_pubkey`, errors);
  expectString(record.ciphertext, `${path}.ciphertext`, errors);
  expectEnum(record.algorithm, `${path}.algorithm`, errors, ["ECIES", "X25519-XSalsa20-Poly1305"]);
  expectString(record.ephemeral_pubkey, `${path}.ephemeral_pubkey`, errors);
}
function validatePrice(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["amount", "token", "chain_id"], path, errors);
  expectString(record.amount, `${path}.amount`, errors);
  expectString(record.token, `${path}.token`, errors);
  expectString(record.chain_id, `${path}.chain_id`, errors);
}
function validateLitCondition(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["chain", "contract", "method", "params", "return_value_test"], path, errors);
  expectString(record.chain, `${path}.chain`, errors);
  expectString(record.contract, `${path}.contract`, errors);
  expectString(record.method, `${path}.method`, errors);
  expectStringArray(record.params, `${path}.params`, errors);
  const returnValueTest = expectRecord(record.return_value_test, `${path}.return_value_test`, errors);
  if (!returnValueTest) {
    return;
  }
  rejectUnknownKeys(returnValueTest, ["comparator", "value"], `${path}.return_value_test`, errors);
  expectEnum(returnValueTest.comparator, `${path}.return_value_test.comparator`, errors, qr20ConditionComparators);
  expectString(returnValueTest.value, `${path}.return_value_test.value`, errors);
}
function validateType5Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "target_cid", "content_type", "price", "creator", "lit_condition"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "unlock_ipfs") {
    pushError(errors, `${path}.action`, 'Expected action to be "unlock_ipfs".');
  }
  expectString(record.target_cid, `${path}.target_cid`, errors);
  expectString(record.content_type, `${path}.content_type`, errors);
  validatePrice(record.price, `${path}.price`, errors);
  expectString(record.creator, `${path}.creator`, errors);
  validateLitCondition(record.lit_condition, `${path}.lit_condition`, errors);
}
function validateEmbeddedIntentReference(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["type", "intent"], path, errors);
  const type = expectInteger(record.type, `${path}.type`, errors, 0);
  if (type !== void 0 && type > 255) {
    pushError(errors, `${path}.type`, "Expected a type <= 255.");
  }
  validateIntentByType(type ?? -1, record.intent, `${path}.intent`, errors);
}
function detectPipelineCycles(steps, path, errors) {
  const visiting = /* @__PURE__ */ new Set();
  const visited = /* @__PURE__ */ new Set();
  const visit = (stepId) => {
    if (visited.has(stepId)) {
      return;
    }
    if (visiting.has(stepId)) {
      pushError(errors, path, `Detected a dependency cycle at step ${stepId}.`);
      return;
    }
    visiting.add(stepId);
    for (const dependency of steps.get(stepId) ?? []) {
      visit(dependency);
    }
    visiting.delete(stepId);
    visited.add(stepId);
  };
  for (const stepId of steps.keys()) {
    visit(stepId);
  }
}
function validateType10Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "steps", "execution_mode", "atomicity", "gas_budget"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "execute_pipeline") {
    pushError(errors, `${path}.action`, 'Expected action to be "execute_pipeline".');
  }
  expectEnum(record.execution_mode, `${path}.execution_mode`, errors, qr20ExecutionModes);
  expectEnum(record.atomicity, `${path}.atomicity`, errors, qr20AtomicityModes);
  const gasBudget = expectRecord(record.gas_budget, `${path}.gas_budget`, errors);
  if (gasBudget) {
    rejectUnknownKeys(gasBudget, ["max_total", "token"], `${path}.gas_budget`, errors);
    expectString(gasBudget.max_total, `${path}.gas_budget.max_total`, errors);
    expectString(gasBudget.token, `${path}.gas_budget.token`, errors);
  }
  const steps = expectArray(record.steps, `${path}.steps`, errors);
  if (!steps) {
    return;
  }
  const dependencyGraph = /* @__PURE__ */ new Map();
  const seenStepIds = /* @__PURE__ */ new Set();
  steps.forEach((step, index) => {
    const stepPath = `${path}.steps[${index}]`;
    const stepRecord = expectRecord(step, stepPath, errors);
    if (!stepRecord) {
      return;
    }
    rejectUnknownKeys(stepRecord, ["step_id", "type", "intent", "depends_on", "on_failure"], stepPath, errors);
    const stepId = expectInteger(stepRecord.step_id, `${stepPath}.step_id`, errors, 1);
    if (stepId !== void 0) {
      if (seenStepIds.has(stepId)) {
        pushError(errors, `${stepPath}.step_id`, `Duplicate step_id ${stepId}.`);
      }
      seenStepIds.add(stepId);
    }
    const intentType = expectInteger(stepRecord.type, `${stepPath}.type`, errors, 0);
    if (intentType !== void 0 && intentType > 255) {
      pushError(errors, `${stepPath}.type`, "Expected a type <= 255.");
    }
    validateIntentByType(intentType ?? -1, stepRecord.intent, `${stepPath}.intent`, errors);
    expectEnum(stepRecord.on_failure, `${stepPath}.on_failure`, errors, qr20FailureModes);
    const dependsOn = expectArray(stepRecord.depends_on, `${stepPath}.depends_on`, errors) ?? [];
    const dependencies = [];
    dependsOn.forEach((dependency, dependencyIndex) => {
      const parsed = expectInteger(dependency, `${stepPath}.depends_on[${dependencyIndex}]`, errors, 1);
      if (parsed !== void 0) {
        dependencies.push(parsed);
      }
    });
    if (stepId !== void 0) {
      if (dependencies.includes(stepId)) {
        pushError(errors, `${stepPath}.depends_on`, "A pipeline step cannot depend on itself.");
      }
      dependencyGraph.set(stepId, dependencies);
    }
  });
  for (const [stepId, dependencies] of dependencyGraph.entries()) {
    dependencies.forEach((dependency) => {
      if (!dependencyGraph.has(dependency)) {
        pushError(errors, `${path}.steps`, `Step ${stepId} depends on unknown step ${dependency}.`);
      }
    });
  }
  detectPipelineCycles(dependencyGraph, `${path}.steps`, errors);
}
function validateAgentParticipant(value, path, errors, sender = false) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, sender ? ["id", "owner", "delegation_proof"] : ["id", "owner"], path, errors);
  expectString(record.id, `${path}.id`, errors);
  if (record.owner !== void 0 || sender) {
    expectString(record.owner, `${path}.owner`, errors);
  }
  if (sender) {
    expectString(record.delegation_proof, `${path}.delegation_proof`, errors);
  }
}
function validateCostLimit(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["amount", "token"], path, errors);
  expectString(record.amount, `${path}.amount`, errors);
  expectString(record.token, `${path}.token`, errors);
}
function validateType11Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "sender_agent", "recipient_agent", "task", "verification"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "agent_request") {
    pushError(errors, `${path}.action`, 'Expected action to be "agent_request".');
  }
  validateAgentParticipant(record.sender_agent, `${path}.sender_agent`, errors, true);
  validateAgentParticipant(record.recipient_agent, `${path}.recipient_agent`, errors);
  const task = expectRecord(record.task, `${path}.task`, errors);
  if (task) {
    rejectUnknownKeys(task, ["description", "constraints", "input_data", "output_schema"], `${path}.task`, errors);
    expectString(task.description, `${path}.task.description`, errors);
    expectString(task.input_data, `${path}.task.input_data`, errors);
    const constraints = expectRecord(task.constraints, `${path}.task.constraints`, errors);
    if (constraints) {
      rejectUnknownKeys(constraints, ["max_cost", "deadline", "required_proofs", "allowed_capabilities"], `${path}.task.constraints`, errors);
      validateCostLimit(constraints.max_cost, `${path}.task.constraints.max_cost`, errors);
      expectIsoTimestamp(constraints.deadline, `${path}.task.constraints.deadline`, errors);
      expectStringArray(constraints.required_proofs, `${path}.task.constraints.required_proofs`, errors);
      if (constraints.allowed_capabilities !== void 0) {
        expectStringArray(constraints.allowed_capabilities, `${path}.task.constraints.allowed_capabilities`, errors);
      }
    }
    const outputSchema = expectRecord(task.output_schema, `${path}.task.output_schema`, errors);
    if (outputSchema && Object.keys(outputSchema).length === 0) {
      pushError(errors, `${path}.task.output_schema`, "Expected output_schema to be non-empty.");
    }
  }
  const verification = expectRecord(record.verification, `${path}.verification`, errors);
  if (verification) {
    rejectUnknownKeys(verification, ["method", "arbitration"], `${path}.verification`, errors);
    expectEnum(verification.method, `${path}.verification.method`, errors, qr20AgentVerificationMethods);
    expectString(verification.arbitration, `${path}.verification.arbitration`, errors);
  }
}
function validateFulfillmentProofData(proofType, value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["tx_hash", "block_number", "chain_id", "merkle_path", "output_hash", "attestation", "signed_output", "zk_proof"], path, errors);
  if (record.tx_hash !== void 0) {
    expectString(record.tx_hash, `${path}.tx_hash`, errors);
  }
  if (record.block_number !== void 0) {
    expectInteger(record.block_number, `${path}.block_number`, errors, 0);
  }
  if (record.chain_id !== void 0) {
    expectString(record.chain_id, `${path}.chain_id`, errors);
  }
  if (record.merkle_path !== void 0) {
    expectStringArray(record.merkle_path, `${path}.merkle_path`, errors);
  }
  if (record.output_hash !== void 0) {
    expectString(record.output_hash, `${path}.output_hash`, errors);
  }
  if (record.attestation !== void 0) {
    expectString(record.attestation, `${path}.attestation`, errors);
  }
  if (record.signed_output !== void 0) {
    expectString(record.signed_output, `${path}.signed_output`, errors);
  }
  if (record.zk_proof !== void 0) {
    expectString(record.zk_proof, `${path}.zk_proof`, errors);
  }
  switch (proofType) {
    case "tx_hash":
      if (typeof record.tx_hash !== "string") {
        pushError(errors, `${path}.tx_hash`, "Expected tx_hash for proof_type=tx_hash.");
      }
      break;
    case "merkle_receipt":
      if (typeof record.tx_hash !== "string" || !Array.isArray(record.merkle_path)) {
        pushError(errors, path, "Expected tx_hash and merkle_path for proof_type=merkle_receipt.");
      }
      break;
    case "zk_proof":
      if (typeof record.zk_proof !== "string" && typeof record.output_hash !== "string") {
        pushError(errors, path, "Expected zk_proof or output_hash for proof_type=zk_proof.");
      }
      break;
    case "oracle_attestation":
      if (typeof record.attestation !== "string") {
        pushError(errors, `${path}.attestation`, "Expected attestation for proof_type=oracle_attestation.");
      }
      break;
    case "signed_output":
      if (typeof record.signed_output !== "string" && typeof record.output_hash !== "string") {
        pushError(errors, path, "Expected signed_output or output_hash for proof_type=signed_output.");
      }
      break;
    default:
      break;
  }
}
function validateType13Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(
    record,
    ["action", "original_intent_id", "resolver_address", "proof_type", "proof_data", "timestamp", "resolver_signature"],
    path,
    errors
  );
  if (expectString(record.action, `${path}.action`, errors) !== "submit_proof") {
    pushError(errors, `${path}.action`, 'Expected action to be "submit_proof".');
  }
  expectIntentIdentifier(record.original_intent_id, `${path}.original_intent_id`, errors);
  expectString(record.resolver_address, `${path}.resolver_address`, errors);
  const proofType = expectEnum(record.proof_type, `${path}.proof_type`, errors, qr20ProofTypes);
  validateFulfillmentProofData(proofType, record.proof_data, `${path}.proof_data`, errors);
  expectIsoTimestamp(record.timestamp, `${path}.timestamp`, errors);
  expectString(record.resolver_signature, `${path}.resolver_signature`, errors);
}
function validateType15Condition(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["type", "source", "comparator", "threshold", "check_interval"], path, errors);
  expectEnum(record.type, `${path}.type`, errors, qr20ConditionTypes);
  expectString(record.source, `${path}.source`, errors);
  expectEnum(record.comparator, `${path}.comparator`, errors, qr20ConditionComparators);
  expectString(record.threshold, `${path}.threshold`, errors);
  const interval = record.check_interval;
  if (typeof interval === "number") {
    expectInteger(interval, `${path}.check_interval`, errors, 0);
  } else {
    expectString(interval, `${path}.check_interval`, errors);
  }
}
function validateType15Intent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  rejectUnknownKeys(record, ["action", "condition", "if_true", "if_false"], path, errors);
  if (expectString(record.action, `${path}.action`, errors) !== "conditional_execute") {
    pushError(errors, `${path}.action`, 'Expected action to be "conditional_execute".');
  }
  validateType15Condition(record.condition, `${path}.condition`, errors);
  validateEmbeddedIntentReference(record.if_true, `${path}.if_true`, errors);
  validateEmbeddedIntentReference(record.if_false, `${path}.if_false`, errors);
}
function validateRegistryOnlyIntent(value, path, errors) {
  const record = expectRecord(value, path, errors);
  if (!record) {
    return;
  }
  if (Object.keys(record).length === 0) {
    pushError(errors, path, "Expected a non-empty intent object.");
  }
}
function validateIntentByType(type, value, path, errors) {
  switch (type) {
    case 0:
      validateType0Intent(value, path, errors);
      return;
    case 1:
      validateType1Intent(value, path, errors);
      return;
    case 5:
      validateType5Intent(value, path, errors);
      return;
    case 10:
      validateType10Intent(value, path, errors);
      return;
    case 11:
      validateType11Intent(value, path, errors);
      return;
    case 13:
      validateType13Intent(value, path, errors);
      return;
    case 15:
      validateType15Intent(value, path, errors);
      return;
    case 2:
    case 3:
    case 4:
    case 6:
    case 7:
    case 8:
    case 9:
    case 12:
    case 14:
    case 16:
    case 17:
    case 18:
    case 19:
      validateRegistryOnlyIntent(value, path, errors);
      return;
    default:
      if (type >= 0 && type <= 255) {
        validateRegistryOnlyIntent(value, path, errors);
        return;
      }
      pushError(errors, path, "Expected a supported QR20 type between 0 and 255.");
  }
}
function validateQr20Intent(type, input) {
  const errors = [];
  validateIntentByType(type, input, "$", errors);
  return result(input, errors);
}
function validateQr20Envelope(input) {
  const errors = [];
  const record = expectRecord(input, "$", errors);
  if (!record) {
    return result(input, errors);
  }
  validateRemovedEnvelopeFields(record, errors);
  rejectUnknownKeys(
    record,
    ["qr", "version", "type", "id", "timestamp", "creator", "routing", "intent", "encryption", "channel", "extensions", "proof"],
    "$",
    errors
  );
  if (expectString(record.qr, "$.qr", errors) !== "20") {
    pushError(errors, "$.qr", 'Expected qr to be "20".');
  }
  if (expectString(record.version, "$.version", errors) !== "1.0.0") {
    pushError(errors, "$.version", 'Expected version to be "1.0.0".');
  }
  const type = expectInteger(record.type, "$.type", errors, 0);
  if (type !== void 0 && type > 255) {
    pushError(errors, "$.type", "Expected a type <= 255.");
  }
  expectIntentIdentifier(record.id, "$.id", errors);
  expectIsoTimestamp(record.timestamp, "$.timestamp", errors);
  validateCreator(record.creator, "$.creator", errors);
  validateRouting(record.routing, "$.routing", errors);
  validateEncryption(record.encryption, "$.encryption", errors);
  const extensions = expectRecord(record.extensions, "$.extensions", errors);
  if (extensions) {
    Object.entries(extensions).forEach(([key, value]) => {
      if (!isJsonValue(value)) {
        pushError(errors, `$.extensions.${key}`, "Expected a JSON-serializable value.");
      }
    });
  }
  validateIntentByType(type ?? -1, record.intent, "$.intent", errors);
  if (record.channel !== void 0) {
    validateChannel(record.channel, "$.channel", errors);
  }
  if (record.proof !== void 0) {
    validateEnvelopeProof(record.proof, "$.proof", errors);
  }
  return result(input, errors);
}
function normalizeJsonValue(value, path) {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError(`${path} contains a non-finite number.`);
    }
    return Object.is(value, -0) ? 0 : value;
  }
  if (Array.isArray(value)) {
    return value.map((entry, index) => normalizeJsonValue(entry, `${path}[${index}]`));
  }
  if (!isRecord(value)) {
    throw new TypeError(`${path} contains a non-JSON value.`);
  }
  const normalizedEntries = Object.entries(value).filter(([, entry]) => entry !== void 0).map(([key, entry]) => [key, normalizeJsonValue(entry, `${path}.${key}`)]).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0);
  return Object.fromEntries(normalizedEntries);
}
function encodeCanonicalJson(value) {
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((entry) => encodeCanonicalJson(entry)).join(",")}]`;
  }
  const entries = Object.entries(value).map(([key, entry]) => `${JSON.stringify(key)}:${encodeCanonicalJson(entry)}`);
  return `{${entries.join(",")}}`;
}
function canonicalizeQr20JsonTs(value) {
  return encodeCanonicalJson(normalizeJsonValue(value, "$"));
}
function canonicalizeQr20Json(value) {
  const normalized = normalizeJsonValue(value, "$");
  const wasm = getWasmCanonicalizer();
  if (wasm !== null) {
    return wasm(JSON.stringify(normalized));
  }
  return encodeCanonicalJson(normalized);
}
function cloneEnvelopeForCanonicalization(envelope, { omitProof = false, omitCreatorSignature = false, omitEnvelopeId = false } = {}) {
  const clonedCreator = omitCreatorSignature ? { ...envelope.creator, signature: "" } : { ...envelope.creator };
  const clonedEnvelope = {
    ...envelope,
    creator: clonedCreator
  };
  if (omitProof) {
    delete clonedEnvelope.proof;
  }
  if (omitEnvelopeId) {
    delete clonedEnvelope.id;
  }
  return clonedEnvelope;
}
function createQr20SigningPayload(envelope) {
  return canonicalizeQr20Json(
    cloneEnvelopeForCanonicalization(envelope, {
      omitProof: true,
      omitCreatorSignature: true
    })
  );
}
function createQr20HashingPayload(envelope) {
  return canonicalizeQr20Json(
    cloneEnvelopeForCanonicalization(envelope, {
      omitProof: true,
      omitCreatorSignature: true,
      omitEnvelopeId: true
    })
  );
}
function bytesToHex(bytes) {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}
function hexToBytes(value) {
  const normalized = value.startsWith("0x") ? value.slice(2) : value;
  if (normalized.length === 0 || normalized.length % 2 !== 0 || /[^0-9a-f]/i.test(normalized)) {
    throw new TypeError("Expected an even-length hex string.");
  }
  const bytes = new Uint8Array(normalized.length / 2);
  for (let index = 0; index < normalized.length; index += 2) {
    bytes[index / 2] = Number.parseInt(normalized.slice(index, index + 2), 16);
  }
  return bytes;
}
var base64UrlAlphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
function encodeBase64Url(bytes) {
  let output = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const chunk = (bytes[index] ?? 0) << 16 | (bytes[index + 1] ?? 0) << 8 | (bytes[index + 2] ?? 0);
    const remaining = bytes.length - index;
    output += base64UrlAlphabet[chunk >> 18 & 63];
    output += base64UrlAlphabet[chunk >> 12 & 63];
    output += remaining > 1 ? base64UrlAlphabet[chunk >> 6 & 63] : "";
    output += remaining > 2 ? base64UrlAlphabet[chunk & 63] : "";
  }
  return output;
}
function decodeBase64Url(value) {
  if (!/^[A-Za-z0-9_-]*$/.test(value)) {
    throw new TypeError("Expected a base64url-encoded string.");
  }
  const padded = value.padEnd(Math.ceil(value.length / 4) * 4, "=");
  const sanitized = padded.replace(/=/g, "A");
  const bytes = [];
  for (let index = 0; index < sanitized.length; index += 4) {
    const chunk = sanitized.slice(index, index + 4);
    const values = Array.from(chunk, (character) => {
      const encoded = base64UrlAlphabet.indexOf(character);
      if (encoded === -1) {
        throw new TypeError("Expected a base64url-encoded string.");
      }
      return encoded;
    });
    const combined = values[0] << 18 | values[1] << 12 | values[2] << 6 | values[3];
    bytes.push(combined >> 16 & 255);
    if (padded[index + 2] !== "=") {
      bytes.push(combined >> 8 & 255);
    }
    if (padded[index + 3] !== "=") {
      bytes.push(combined & 255);
    }
  }
  return Uint8Array.from(bytes);
}
var Qr20Error = class extends Error {
  hint;
  remediation;
  constructor(message, options = {}) {
    super(message, options.cause !== void 0 ? { cause: options.cause } : void 0);
    this.name = "Qr20Error";
    if (options.hint !== void 0) {
      this.hint = options.hint;
    }
    if (options.remediation !== void 0) {
      this.remediation = options.remediation;
    }
  }
  /**
   * Plain-object projection suitable for JSON logging or MCP
   * transport. Undefined fields are omitted so downstream consumers
   * can reliably `hasOwnProperty`-check.
   */
  toJSON() {
    const shape = {
      code: this.code,
      message: this.message
    };
    if (this.hint !== void 0) {
      shape.hint = this.hint;
    }
    if (this.remediation !== void 0) {
      shape.remediation = this.remediation;
    }
    return shape;
  }
};
var Qr20SignatureRoutingError = class extends Qr20Error {
  code = "QR20_SIGNATURE_ROUTING_UNRESOLVED";
  constructor(message, options = {}) {
    super(message, options);
    this.name = "Qr20SignatureRoutingError";
  }
};
function isEip155Chain(chain) {
  if (chain.startsWith("eip155:")) {
    return chain.length > "eip155:".length;
  }
  return /^[0-9]+$/.test(chain);
}
function resolveQr20SignatureScheme(creator) {
  const { address, chain, encoding } = creator;
  if (typeof address !== "string" || address.length === 0) {
    throw new Qr20SignatureRoutingError("Creator address is required for signature routing.", {
      hint: "creator.address is missing, not a string, or empty.",
      remediation: "Set creator.address to a non-empty string (e.g. a 0x EVM address or an ed25519: prefixed key)."
    });
  }
  if (typeof chain !== "string" || chain.length === 0) {
    throw new Qr20SignatureRoutingError("Creator chain is required for signature routing.", {
      hint: "creator.chain is missing, not a string, or empty.",
      remediation: 'Set creator.chain to a CAIP-2 identifier (e.g. "eip155:8453", "solana:mainnet") or a legacy numeric chain id.'
    });
  }
  const isHexAddress = address.startsWith("0x");
  const eip155 = isEip155Chain(chain);
  if (encoding === "eip712") {
    if (!isHexAddress || !eip155) {
      throw new Qr20SignatureRoutingError(
        "encoding='eip712' requires an 0x-prefixed address on an eip155 chain.",
        {
          hint: `encoding='eip712' was set but address='${address}' / chain='${chain}' is not an EIP-155 EVM account.`,
          remediation: "Either remove creator.encoding or change creator to an 0x-prefixed address on an eip155:* chain."
        }
      );
    }
    return { scheme: "eip712", reason: "encoding='eip712' on eip155 chain" };
  }
  if (isHexAddress && eip155) {
    return { scheme: "secp256k1", reason: "0x address on eip155 chain" };
  }
  if (address.startsWith("ed25519:")) {
    return { scheme: "ed25519", reason: "address prefix 'ed25519:'" };
  }
  if (chain.startsWith("solana:")) {
    return { scheme: "ed25519", reason: "solana CAIP-2 chain" };
  }
  if (chain.startsWith("polkadot:")) {
    return { scheme: "ed25519", reason: "polkadot CAIP-2 chain" };
  }
  if (address.startsWith("did:")) {
    throw new Qr20SignatureRoutingError(
      "DID-based signature routing requires a DID method catalogue, which is not yet implemented.",
      {
        hint: `creator.address='${address}' is a DID, but the DID method catalogue is deferred to Epic F.`,
        remediation: "Use an 0x EVM address or an ed25519: prefixed address for v1.x signature routing."
      }
    );
  }
  throw new Qr20SignatureRoutingError(
    `Cannot deterministically resolve signature scheme for address='${address}' chain='${chain}'.`,
    {
      hint: "No routing rule (eip712 / secp256k1 / ed25519-prefix / solana-chain / polkadot-chain) matched.",
      remediation: "Use one of: 0x address + eip155:* chain; ed25519: prefixed address; or solana:* / polkadot:* chain family."
    }
  );
}
function getSignatureAlgorithmName(algorithm) {
  return algorithm.toLowerCase().replace("-", "");
}
function encodeUtf8(value) {
  return Uint8Array.from(new TextEncoder().encode(value));
}
function toBufferSource(value) {
  const bytes = Uint8Array.from(value);
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return new DataView(buffer);
}
function isCryptoKey(value) {
  return isRecord(value) && "type" in value && "algorithm" in value && "usages" in value;
}
async function importVerificationKey(crypto, suite) {
  if (isCryptoKey(suite.publicKey)) {
    return suite.publicKey;
  }
  switch (suite.name) {
    case "Ed25519":
      return crypto.subtle.importKey("jwk", suite.publicKey, { name: "Ed25519" }, false, ["verify"]);
    case "ECDSA":
      return crypto.subtle.importKey(
        "jwk",
        suite.publicKey,
        { name: "ECDSA", namedCurve: suite.namedCurve },
        false,
        ["verify"]
      );
    case "RSA-PSS":
      return crypto.subtle.importKey(
        "jwk",
        suite.publicKey,
        { name: "RSA-PSS", hash: suite.hash },
        false,
        ["verify"]
      );
  }
}
function decodeSignature(signature, encoding) {
  if (signature instanceof Uint8Array) {
    return signature;
  }
  if (encoding === "hex") {
    return hexToBytes(signature);
  }
  return decodeBase64Url(signature);
}
async function hashQr20Payload(payload, options) {
  const algorithm = options.algorithm ?? "SHA-256";
  const canonicalPayload = typeof payload === "string" ? payload : canonicalizeQr20Json(payload);
  const digest = await options.crypto.subtle.digest(algorithm, toBufferSource(encodeUtf8(canonicalPayload)));
  return bytesToHex(new Uint8Array(digest));
}
async function deriveQr20ContentId(envelope, options) {
  const digest = await hashQr20Payload(createQr20HashingPayload(envelope), options);
  return `${getSignatureAlgorithmName(options.algorithm ?? "SHA-256")}:${digest}`;
}
async function verifyQr20EnvelopeSignature(envelope, options) {
  const signingPayload = createQr20SigningPayload(envelope);
  const contentId = await deriveQr20ContentId(envelope, { crypto: options.crypto, algorithm: "SHA-256" });
  const signatureValue = options.signature ?? envelope.creator.signature;
  try {
    const signatureBytes = decodeSignature(signatureValue, options.signatureEncoding ?? "base64url");
    const verificationKey = await importVerificationKey(options.crypto, options.suite);
    const signedData = toBufferSource(encodeUtf8(signingPayload));
    let verified = false;
    switch (options.suite.name) {
      case "Ed25519":
        verified = await options.crypto.subtle.verify("Ed25519", verificationKey, toBufferSource(signatureBytes), signedData);
        break;
      case "ECDSA":
        verified = await options.crypto.subtle.verify(
          { name: "ECDSA", hash: options.suite.hash },
          verificationKey,
          toBufferSource(signatureBytes),
          signedData
        );
        break;
      case "RSA-PSS":
        verified = await options.crypto.subtle.verify(
          { name: "RSA-PSS", saltLength: options.suite.saltLength },
          verificationKey,
          toBufferSource(signatureBytes),
          signedData
        );
        break;
    }
    if (!verified) {
      return {
        ok: false,
        contentId,
        signingPayload,
        error: "Signature verification failed."
      };
    }
    if (options.attestation) {
      let attestation;
      try {
        let resolveOptions;
        if (options.attestationClaimTypes || options.attestationSignal) {
          resolveOptions = {};
          if (options.attestationClaimTypes) {
            resolveOptions.claimTypes = options.attestationClaimTypes;
          }
          if (options.attestationSignal) {
            resolveOptions.signal = options.attestationSignal;
          }
        }
        attestation = await options.attestation.resolveAttestations(envelope.creator.address, resolveOptions);
      } catch (providerError) {
        attestation = {
          status: "error",
          reason: providerError instanceof Error ? providerError.message : "Attestation provider threw a non-Error value."
        };
      }
      return { ok: true, contentId, signingPayload, attestation };
    }
    return { ok: true, contentId, signingPayload };
  } catch (error) {
    return {
      ok: false,
      contentId,
      signingPayload,
      error: error instanceof Error ? error.message : "Signature verification failed."
    };
  }
}
var Qr20EnvelopeNotSignedError = class extends Qr20Error {
  code = "QR20_ENVELOPE_NOT_SIGNED";
  constructor(message = "Envelope creator.signature is empty; envelope has not been signed.", options = {
    hint: "creator.signature is empty, whitespace-only, or missing on the envelope.",
    remediation: "Call finalizeQr20Envelope(draft, signature) before exporting or submitting the envelope."
  }) {
    super(message, options);
    this.name = "Qr20EnvelopeNotSignedError";
  }
};
function hasNonEmptySignature(envelope) {
  const signature = envelope.creator?.signature;
  return typeof signature === "string" && signature.trim().length > 0;
}
function isSignedQr20Envelope(envelope) {
  return hasNonEmptySignature(envelope);
}
function markQr20EnvelopeAsDraft(envelope) {
  return envelope;
}
function markQr20EnvelopeAsSigned(envelope) {
  if (!hasNonEmptySignature(envelope)) {
    throw new Qr20EnvelopeNotSignedError();
  }
  return envelope;
}
function finalizeQr20Envelope(draft, signature) {
  if (typeof signature !== "string" || signature.trim().length === 0) {
    throw new Qr20EnvelopeNotSignedError(
      "finalizeQr20Envelope requires a non-empty signature string.",
      {
        hint: "The `signature` argument to finalizeQr20Envelope was empty, whitespace-only, or not a string.",
        remediation: "Pass the output of your signer (e.g. base64url- or hex-encoded signature bytes) as the second argument."
      }
    );
  }
  const signed = {
    ...draft,
    creator: {
      ...draft.creator,
      signature
    }
  };
  return signed;
}
export {
  QR20_REMOVED_FIELD_MIGRATION_CODE,
  Qr20EnvelopeNotSignedError,
  Qr20Error,
  Qr20SignatureRoutingError,
  __resetCanonicalizerForTest,
  canonicalizeQr20Json,
  canonicalizeQr20JsonTs,
  createQr20HashingPayload,
  createQr20SigningPayload,
  decodeBase64Url,
  deriveQr20ContentId,
  encodeBase64Url,
  ensureCanonicalizerReady,
  finalizeQr20Envelope,
  hashQr20Payload,
  isSignedQr20Envelope,
  markQr20EnvelopeAsDraft,
  markQr20EnvelopeAsSigned,
  qr20NoopAttestationProvider,
  resolveQr20SignatureScheme,
  validateQr20Envelope,
  validateQr20Intent,
  verifyQr20EnvelopeSignature
};
